import React, { useEffect, useRef, useState } from "react";
import { Bell, X, CheckCircle2, Trash2, PenLine, UserPlus, Loader2 } from "lucide-react";
import * as api from "../lib/api";

const ICON_BY_KIND = {
  task_modified: PenLine,
  task_deleted: Trash2,
  task_shared: UserPlus,
};

export default function NotificationsBell({ pollMs = 30000 }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const d = await api.listNotifications();
      setData(d);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, pollMs);
    return () => clearInterval(iv);
  }, [pollMs]);

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      setLoading(true);
      try {
        await api.readAllNotifications();
        await load();
      } finally { setLoading(false); }
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteNotification(id);
      await load();
    } catch (e) { /* ignore */ }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMin = Math.floor((now - d) / 60000);
      if (diffMin < 1) return "adesso";
      if (diffMin < 60) return `${diffMin} min fa`;
      if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h fa`;
      return d.toLocaleDateString("it-IT");
    } catch { return ""; }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={toggle}
        className="relative p-2 hover:bg-slate-100 rounded-full transition"
        title="Notifiche"
        data-testid="notifications-bell"
      >
        <Bell size={20} strokeWidth={1.75} className="text-slate-700" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center shadow" data-testid="notif-badge">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden fade-in" data-testid="notif-dropdown">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-outfit font-bold text-slate-900 text-sm">Notifiche</span>
            {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
          </div>
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Nessuna notifica</div>
            ) : (
              data.items.map((n) => {
                const Icon = ICON_BY_KIND[n.kind] || CheckCircle2;
                return (
                  <div key={n.id} className={`p-3 border-b border-slate-50 hover:bg-slate-50 group ${!n.read ? "bg-blue-50/50" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-full shrink-0 ${
                        n.kind === "task_deleted" ? "bg-red-100 text-red-600" :
                        n.kind === "task_modified" ? "bg-amber-100 text-amber-600" :
                        "bg-blue-100 text-blue-600"
                      }`}>
                        <Icon size={13} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-slate-900">{n.title}</div>
                        <div className="text-xs text-slate-600 mt-0.5 leading-tight">{n.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{fmt(n.ts)}</div>
                      </div>
                      <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition shrink-0">
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
